/**
 * =========================================================================================
 * UDownloadPopupList - Global Download Notification Sidebar
 * =========================================================================================
 * Shared component for displaying download request reports across all products.
 **/

import React, {useEffect, useState} from "react";
import * as Icon from 'ubs-icons';
import UCustomScrollBar from '../UCustomScrollBar';
import UModal from 'ubs-framework/v2/UModal/NewUModal';
import UNoDataFound from 'ubs-framework/v2/UErrorPages/UNoDataFound'
import UPopup from "ubs-framework/v2/UPopup";
import Skeleton from '../skeleton';
import {COLOR, FORMAT} from "../common/constant";
import {dateFormatter, downloadFile} from "../common/function";

const DOWNLOAD_STATUS = [
    {label: 'Pending', value: 1},
    {label: 'Failed', value: 2},
    {label: 'Success', value: 3}
];

const THREE_HOURS_MS = 3 * 3600 * 1000;

const UDownloadPopupList = (props) => {
    const {useSocket, sendData, apiEvent = 'hrms|reports|logs|list', module: moduleName, extraFilter = {}, userId} = props;
    const _ID = userId;

    const [list, setList] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    const hideDownloadSystem = () => {
        props.setShowDownloadIcon(false);
        props.setDownloadCount(0);
        try { localStorage.removeItem('download_count'); } catch(e) {}
        document.cookie = "download_activity=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    useEffect(() => {
        on();
        const filter = { user: [_ID], ...extraFilter };
        if (moduleName) filter.module = moduleName;
        sendData(apiEvent, { limit: 20, currentPage: 1, filter });
        return () => {
            off();
            if (window.__downloadHideTimer) clearTimeout(window.__downloadHideTimer);
        }
    }, [])

    const {off, on} = useSocket(res => {
        if (res && !res?.error && !res?.data?.error) {
            const isDownloadEvent = res?.data?.url || (res?.data?.message && (res?.data?.message?.toLowerCase?.()?.includes('export') || res?.data?.message?.toLowerCase?.()?.includes('downloading')));
            
            if (isDownloadEvent) {
                const filter = { user: [_ID], ...extraFilter };
                if (moduleName) filter.module = moduleName;
                sendData(apiEvent, { limit: 20, currentPage: 1, filter });
                
                // If the popup is closed, increment the unread count
                if (!props.openDownloadPopup) {
                    props.setDownloadCount(prev => {
                        const newCount = prev + 1;
                        try { localStorage.setItem('download_count', newCount); } catch(e) {}
                        return newCount;
                    });
                }
            }

            switch (res?.event) {
                case apiEvent: {
                    const resultList = res?.data?.result || [];
                    if (res?.data?.result) {
                        setList(resultList);
                        setDataLoading(false);
                    }
                    
                    // 3 Hours Activity Window
                    if (resultList.length > 0) {
                        const latestRecord = resultList[0];
                        const lastTime = new Date(latestRecord.createdAt).getTime();
                        const now = new Date().getTime();
                        const diff = now - lastTime;
                        
                        if (diff < THREE_HOURS_MS) {
                            const remainingMs = THREE_HOURS_MS - diff;
                            document.cookie = `download_activity=true; max-age=${Math.floor(remainingMs/1000)}; path=/`;
                            props.setShowDownloadIcon(true);

                            // Real-time auto-hide timer
                            if (window.__downloadHideTimer) clearTimeout(window.__downloadHideTimer);
                            window.__downloadHideTimer = setTimeout(() => {
                                hideDownloadSystem();
                            }, remainingMs);
                        } else {
                            hideDownloadSystem();
                        }
                    } else {
                        hideDownloadSystem();
                    }
                    break;
                }
                default:
                    break;
            }
        }
    });

    const handle = {
        /* downloadFile force-downloads via a blob, so browser-viewable exports (PDF especially)
           land as a file instead of opening in a tab. */
        download: (data) => {
            if (data?.url) {
                downloadFile(data?.url, data?.name)
            }
        }
    }

    // Close on ESC key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27) props?.setOpenDownloadPopup(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        if (!props?.openDownloadPopup) return;
        const hasPending = list?.some(item => item?.status === 1);
        if (!hasPending) return;

        const intervalId = setInterval(() => {
            const filter = { user: [_ID], ...extraFilter };
            if (moduleName) filter.module = moduleName;
            sendData(apiEvent, { limit: 20, currentPage: 1, filter });
        }, 5000);

        return () => clearInterval(intervalId);
    }, [props?.openDownloadPopup, list]);

    return (
        <React.Fragment>
            {props?.openDownloadPopup && (
                <div className="u_notification-side-modal">
                    <UModal
                        open={props?.openDownloadPopup}
                        closeOnOutsideClick
                        className={'u_sidebar-modal'}
                        maxWidth={450}
                        onClose={() => {
                            props?.setOpenDownloadPopup(false)
                        }}
                        header={
                            <div className="u_flex u_align-center u_content-between u_w-full u_pr-35">
                                <h2 className="u_mb-0 u_color-title u_font-16">Download Request Reports</h2>
                                {props.downloadCount !== 0 && (
                                    <p className="u_cp u_flex u_align-center u_mb-0 u_font-12 u_view-more" onClick={() => { props.setDownloadCount(0); try { localStorage.removeItem('download_count'); } catch(e) {} }}>
                                        <span className="u_pr-5 u_flex"><Icon.DoubleTick size={14} fill={COLOR.u_green} /></span>
                                        Mark all as read
                                    </p>
                                )}
                            </div>
                        }
                        position="right"
                    >
                        <div className="u_notification">
                            <UCustomScrollBar>
                                <div className="u_notification-inner u_h-full">
                                    {
                                        (list?.filter(item => (new Date().getTime() - new Date(item.createdAt).getTime()) < THREE_HOURS_MS))?.length !== 0 || dataLoading ?
                                            <ul className="u_h-full">

                                                {dataLoading ? Array.from(Array(8).keys())?.map((key, i) => {
                                                        return (
                                                            <li className="u_content-between" key={i}>
                                                                <div className="u_users-details u_w-full">
                                                                    <Skeleton width="100%" height="16px" />
                                                                    <Skeleton width="60%" height="12px" className="u_mt-5" />
                                                                </div>
                                                            </li>
                                                        )
                                                    })
                                                    :
                                                    list.filter(item => (new Date().getTime() - new Date(item.createdAt).getTime()) < THREE_HOURS_MS).map(data => {
                                                        return (
                                                            <li className={`u_cp u_flex u_align-center u_content-between`} key={data?._id}>
                                                                <div className="u_flex u_align-center u_flex-1">
                                                                    <UPopup
                                                                        params={{className: "u_tooltip"}}
                                                                        desc={<span>{DOWNLOAD_STATUS.find(opt => opt.value == data?.status)?.label || 'Pending'}</span>}
                                                                    >
                                                                        <span className={`u_dot u_mr-10 u_flex-shrink ${(data.status === 2) ? 'u_bg-pastelred' : (data.status === 3) ? 'u_bg-green' : 'u_bg-orange'}`}/>
                                                                    </UPopup>
                                                                    <div className="u_notification-title u_notification-block u_flex-1">
                                                                        <h5 className="u_one-line u_mb-0 u_font-14 u_fw-600 u_color-title" title={data?.name}>{data?.name}</h5>
                                                                        <p className="u_font-12 u_pt-4 u_color-gray u_mb-0">
                                                                            {data?.createdAt && dateFormatter({date: data?.createdAt, format: FORMAT.FULL_DATE_TIME})}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="u_flex u_align-center u_ml-10">
                                                                    {data.status === 3 && !!data?.url?.length && (
                                                                        <UPopup
                                                                            params={{className: "u_tooltip", position: "bottom right", keepTooltipInside: false}}
                                                                            desc={<span>Download File</span>}
                                                                        >
                                                                            <span className="u_flex u_cp u_icon-rollover" onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handle.download(data);
                                                                            }}>
                                                                                <Icon.Download size={18} fill={COLOR.u_blue} />
                                                                            </span>
                                                                        </UPopup>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        )
                                                    })}
                                            </ul>
                                            :
                                            <div className="u_flex u_align-center u_content-center u_h-full">
                                                <UNoDataFound message="No Download Requests Found" />
                                            </div>
                                    }
                                </div>
                            </UCustomScrollBar>
                        </div>
                    </UModal>
                </div>
            )}
        </React.Fragment>
    );
}

export default UDownloadPopupList;
